import { api } from '../api/client.js';
import { getVisitorId, newSessionId } from './visitor.js';

interface SiteEventPayload {
  type: string;
  visitorId: string;
  sessionId: string;
  path: string;
  title?: string;
  referrer?: string;
  target?: string;
  eventId?: string;
  [extra: string]: unknown;
}

const MAX_QUEUE = 50;
const FLUSH_INTERVAL_MS = 5000;

/** Keep dynamic identifiers and query-string values out of site analytics paths. */
export function normalizeSitePath(pathname: string): string {
  if (pathname === '/') return '/';
  if (/^\/r\/[^/]+/.test(pathname)) return '/r/:linkId';
  if (/^\/dashboard\/[^/]+/.test(pathname)) return '/dashboard/:proposalId';
  if (/^\/edit\/[^/]+/.test(pathname)) return '/edit/:proposalId';
  if (/^\/legal\/[^/]+/.test(pathname)) return '/legal/:doc';
  return pathname.replace(/\/$/, '') || '/';
}

function clickTarget(element: HTMLElement): string | null {
  const explicit = element.dataset.analyticsTarget;
  if (explicit) return explicit.slice(0, 200);

  if (element instanceof HTMLAnchorElement) {
    const href = element.getAttribute('href');
    if (!href || href.startsWith('#')) return href?.slice(0, 200) ?? null;
    try {
      const url = new URL(href, window.location.href);
      if (url.origin === window.location.origin) return normalizeSitePath(url.pathname);
      return `external:${url.hostname}`.slice(0, 200);
    } catch {
      return href.slice(0, 200);
    }
  }

  const label = (element.getAttribute('aria-label') ?? element.textContent ?? '').trim();
  return label ? `button:${label.slice(0, 140)}` : null;
}

export class SiteTracker {
  readonly visitorId: string;
  readonly sessionId: string;
  private readonly queue: SiteEventPayload[] = [];
  private readonly referrer: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private activePath: string | null = null;
  private enteredAt = 0;
  private sequence = 0;

  constructor() {
    this.visitorId = getVisitorId();
    this.sessionId = newSessionId();
    this.referrer = typeof document !== 'undefined' ? document.referrer.slice(0, 1000) : '';
  }

  start(): void {
    if (this.flushTimer) return;
    this.flushTimer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('click', this.onClick, true);
    window.addEventListener('beforeunload', this.onUnload);
  }

  stop(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = null;
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('click', this.onClick, true);
    window.removeEventListener('beforeunload', this.onUnload);
    void this.flush(true);
  }

  pageView(pathname: string, title = document.title): void {
    const path = normalizeSitePath(pathname);
    if (path === '/r/:linkId') {
      if (this.activePath) {
        this.enqueue('page_leave', {
          path: this.activePath,
          durationMs: Math.max(0, Date.now() - this.enteredAt),
        });
      }
      this.activePath = null;
      this.enteredAt = 0;
      return;
    }

    if (this.activePath) {
      this.enqueue('page_leave', {
        path: this.activePath,
        durationMs: Math.max(0, Date.now() - this.enteredAt),
      });
    }
    this.activePath = path;
    this.enteredAt = Date.now();
    this.enqueue('page_view', {
      path,
      title: title.slice(0, 300),
      ...(this.referrer ? { referrer: this.referrer } : {}),
    });
  }

  track(type: string, data: Record<string, unknown> = {}): void {
    if (
      this.activePath === '/r/:linkId' ||
      normalizeSitePath(window.location.pathname) === '/r/:linkId'
    ) {
      return;
    }
    this.enqueue(type, data);
  }

  private enqueue(type: string, data: Record<string, unknown> = {}): void {
    if (this.queue.length >= MAX_QUEUE) this.queue.shift();
    this.queue.push({
      type: type.slice(0, 80),
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      path: this.activePath ?? normalizeSitePath(window.location.pathname),
      eventId: `${this.sessionId}:${this.sequence++}`,
      ...data,
    });
    if (this.queue.length >= MAX_QUEUE) void this.flush();
  }

  private onClick = (event: MouseEvent): void => {
    if (normalizeSitePath(window.location.pathname) === '/r/:linkId') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const element = target.closest<HTMLElement>('a,button,[role="button"]');
    if (
      !element ||
      element.hasAttribute('disabled') ||
      element.getAttribute('aria-disabled') === 'true'
    )
      return;
    if (element.closest('[data-analytics-ignore]')) return;
    const label = clickTarget(element);
    if (label) this.enqueue('click', { target: label });
  };

  private onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      if (this.activePath) {
        this.enqueue('page_leave', {
          path: this.activePath,
          durationMs: Math.max(0, Date.now() - this.enteredAt),
        });
        this.enteredAt = Date.now();
      }
      void this.flush(true);
    }
  };

  private onUnload = (): void => {
    if (this.activePath) {
      this.enqueue('page_leave', {
        path: this.activePath,
        durationMs: Math.max(0, Date.now() - this.enteredAt),
      });
    }
    void this.flush(true);
  };

  async flush(useBeacon = false): Promise<void> {
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.queue.length);
    const payload = JSON.stringify({ events });
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon('/api/site-events', new Blob([payload], { type: 'application/json' }));
      return;
    }
    try {
      await api.postSiteEvents(events);
    } catch {
      // Analytics must never block navigation or product usage.
    }
  }
}

let activeTracker: SiteTracker | null = null;

export function setActiveSiteTracker(tracker: SiteTracker | null): void {
  activeTracker = tracker;
}

/** Record a named conversion from any route without coupling it to the tracker UI. */
export function trackSiteEvent(type: string, data: Record<string, unknown> = {}): void {
  activeTracker?.track(type, data);
}
